import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  Grid, 
  Chip, 
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip
} from '@mui/material';
import { useParams } from "react-router-dom";
import { fetchDataSourcesOverview } from './api';
import { 
  Globe, 
  Storefront, 
  Calendar, 
  Package, 
  TrendUp,
  Database
} from '@phosphor-icons/react';

function DataSourcesOverview() {
  const { projectKey } = useParams();
  
  // State for data
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for processed data
  const [countries, setCountries] = useState([]);
  const [retailerData, setRetailerData] = useState({});
  const [summaryStats, setSummaryStats] = useState({
    total_countries: 0,
    total_retailers: 0,
    total_records: 0
  });

  // Fetch data from the data sources overview API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetchDataSourcesOverview();
        
        if (response) {
          const { data: apiData, total_countries, total_retailers, total_records } = response;
          
          setData(apiData);
          setSummaryStats({ total_countries, total_retailers, total_records });
          
          // Process data for countries and retailers
          processDataForOverview(apiData);
        } else {
          setError('Failed to load data sources overview');
        }
        
      } catch (err) {
        console.error('Error fetching data sources overview:', err);
        setError('Failed to load data sources overview');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const processDataForOverview = (apiData) => {
    // Extract unique countries from the API data
    const uniqueCountries = [...new Set(apiData.map(item => item.country).filter(Boolean))];
    setCountries(uniqueCountries.sort());

    // Process retailer data for all combinations (keeping original structure)
    const retailerStats = {};
    
    // Define the retailers and countries we want to track (original structure)
    const targetRetailers = ['Ploom', 'Glo', 'Iqos'];
    const targetCountries = ['RO', 'IT', 'UK', 'CZ', 'PL'];
    
    // Match API country (may be "RO", "Romania", "ro", etc.) to our target code
    const countryMatches = (apiCountry, targetCode) => {
      if (!apiCountry) return false;
      const c = String(apiCountry).trim().toLowerCase();
      const aliases = {
        RO: ['ro', 'romania'],
        IT: ['it', 'italy', 'italia'],
        UK: ['uk', 'gb', 'united kingdom', 'great britain'],
        CZ: ['cz', 'czech', 'czechia', 'czech republic'],
        PL: ['pl', 'poland', 'polska'],
      };
      const list = aliases[targetCode] || [targetCode.toLowerCase()];
      return list.includes(c) || c === targetCode.toLowerCase();
    };

    // Create all combinations of retailers and countries
    targetRetailers.forEach(retailer => {
      targetCountries.forEach(country => {
        const combinationKey = `${retailer}_${country}`;
        
        // Find matching data from API (handles RO/romania, IT/italy, etc.)
        const matchingData = apiData.find(item => 
          item.retailer && item.retailer.toLowerCase().includes(retailer.toLowerCase()) &&
          countryMatches(item.country, country)
        );
        
        // Get record count from API data, or use 0 for Glo UK
        let recordCount;
        if (retailer === 'Glo' && country === 'UK') {
          recordCount = 0; // Glo UK has 0 records (as per original)
        } else {
          recordCount = matchingData ? matchingData.count : 0;
        }
        
        // Determine data types available
        const dataTypes = [];
        if (retailer === 'Glo' && country === 'UK') {
          // Glo UK has no data types (disabled)
          dataTypes.push('No data available');
        } else {
          // All other combinations get all three data types
          dataTypes.push('Price data', 'Out of stock data', 'Visibility data');
        }
       
        // Special handling for specific combinations (keeping original logic)
        let status = 'Running';
        let isDisabled = false;
        let isInProgress = false;
        let note = '';
        
        if (retailer === 'Glo' && country === 'UK') {
          status = 'Not running';
          isDisabled = true;
          note = 'Glo UK website not found.';
        }
        
        // Use min date from dataset when available; fallback for disabled/no-data
        const minDate = matchingData?.min_date
          ? (typeof matchingData.min_date === 'string'
              ? matchingData.min_date
              : matchingData.min_date?.slice?.(0, 10) ?? '—')
          : '—';
        
        // Daily counts for last 10 days (Airflow-style); [0,0,...] for no-data sources
        const dailyCounts = matchingData?.daily_counts ?? Array(10).fill(0);
        const dailyDates = (matchingData?.daily_dates?.length >= 10)
          ? matchingData.daily_dates
          : getLast10Dates();
        
        retailerStats[combinationKey] = {
          retailer: retailer,
          country: country,
          recordCount: recordCount,
          scrapingStartDate: minDate,
          status: status,
          hasData: recordCount > 0,
          dataTypes: dataTypes,
          isDisabled: isDisabled,
          isInProgress: isInProgress,
          note: note,
          dailyCounts,
          dailyDates
        };
      });
    });
    
    setRetailerData(retailerStats);
  };

  const getRetailerColor = (retailer) => {
    switch (retailer.toLowerCase()) {
      case 'ploom':
        return '#4CAF50'; // Green
      case 'glo':
        return '#2196F3'; // Blue
      case 'iqos':
        return '#FF9800'; // Orange
      default:
        return '#9E9E9E'; // Gray
    }
  };

  const getRetailerIcon = (retailer) => {
    switch (retailer.toLowerCase()) {
      case 'ploom':
        return '🟢';
      case 'glo':
        return '🔵';
      case 'iqos':
        return '🟠';
      default:
        return '⚪';
    }
  };

  // Airflow-style day status: green = Success, yellow = Pending, grey = Missing
  const getDayStatusColor = (count) => {
    if (count >= 5) return '#4CAF50';   // green - Success
    if (count >= 1) return '#FFC107';   // yellow - Pending
    return '#9E9E9E';                    // grey - Missing
  };

  const getDayStatusLabel = (count) => {
    if (count >= 5) return 'Success';
    if (count >= 1) return 'Pending';
    return 'Missing';
  };

  // Last 10 days (oldest to newest) - used when API doesn't provide daily_dates
  const getLast10Dates = () => {
    const dates = [];
    for (let i = 9; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      dates.push(d.toISOString().slice(0, 10));
    }
    return dates;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        Data Sources Overview
      </Typography>

      {/* Summary Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ mb: 1 }}>
              <Globe size={24} color="#2196F3" />
              <Typography variant="h6" color="primary">
                {summaryStats.total_countries}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Countries
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ mb: 1 }}>
              <Storefront size={24} color="#4CAF50" />
              <Typography variant="h6" color="primary">
                {summaryStats.total_retailers}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Retailers
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ textAlign: 'center', p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="center" gap={1} sx={{ mb: 1 }}>
              <Database size={24} color="#FF9800" />
              <Typography variant="h6" color="primary">
                {summaryStats.total_records.toLocaleString()}
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary">
              Total Records
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Data Sources Table */}
      <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
        Data Sources by Retailer & Country
      </Typography>
      <Box sx={{ mb: 1, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant="caption" color="text.secondary">
          Last 10 days status:
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#4CAF50' }} />
          <Typography variant="caption" color="text.secondary">Success</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#FFC107' }} />
          <Typography variant="caption" color="text.secondary">Pending</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Box sx={{ width: 10, height: 10, borderRadius: 0.5, bgcolor: '#9E9E9E' }} />
          <Typography variant="caption" color="text.secondary">Missing</Typography>
        </Box>
      </Box>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size="small" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: 'action.hover' }}>
              <TableCell><strong>Retailer</strong></TableCell>
              <TableCell><strong>Country</strong></TableCell>
              <TableCell align="right"><strong>Records</strong></TableCell>
              <TableCell><strong>Last 10 days status</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
              <TableCell><strong>Started</strong></TableCell>
              <TableCell><strong>Data Types</strong></TableCell>
              <TableCell><strong>Note</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(retailerData).map(([combinationKey, stats]) => (
              <TableRow
                key={combinationKey}
                sx={{
                  opacity: stats.isDisabled ? 0.6 : 1,
                  backgroundColor: stats.isDisabled ? '#ffebee' : 'inherit',
                  borderLeft: `4px solid ${stats.isDisabled ? '#f44336' : getRetailerColor(stats.retailer)}`,
                  '&:hover': {
                    backgroundColor: stats.isDisabled ? '#ffebee' : 'action.hover'
                  }
                }}
              >
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{getRetailerIcon(stats.retailer)}</Typography>
                    <Typography variant="body2" sx={{ color: getRetailerColor(stats.retailer), fontWeight: 500 }}>
                      {stats.retailer}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>{stats.country}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2"><strong>{stats.recordCount.toLocaleString()}</strong></Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.25} alignItems="center">
                    {(stats.dailyCounts ?? []).map((count, i) => {
                      const date = (stats.dailyDates ?? getLast10Dates())[i] ?? '—';
                      const status = getDayStatusLabel(count);
                      const records = count ?? 0;
                      const tooltipText = `${date} – ${status} (${records} records)`;
                      return (
                        <Tooltip
                          key={i}
                          title={tooltipText}
                          arrow
                          placement="top"
                        >
                          <span style={{ display: 'inline-block', lineHeight: 0 }}>
                            <Box
                              component="span"
                              sx={{
                                width: 14,
                                height: 14,
                                borderRadius: 0.5,
                                backgroundColor: getDayStatusColor(count),
                                minWidth: 14,
                                display: 'inline-block',
                              }}
                            />
                          </span>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        backgroundColor: stats.status === 'Running' ? '#4CAF50' : '#f44336'
                      }}
                    />
                    <Typography variant="body2">{stats.status}</Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{stats.scrapingStartDate}</Typography>
                </TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {stats.dataTypes.map((dataType, index) => (
                      <Chip
                        key={index}
                        label={dataType}
                        size="small"
                        color={dataType === 'No data available' ? "error" : "default"}
                        variant="outlined"
                        sx={{ fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  {stats.note ? (
                    <Typography variant="body2" color="error" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                      {stats.note}
                    </Typography>
                  ) : (
                    <Typography variant="body2" color="text.secondary">—</Typography>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

export default DataSourcesOverview; 